<template>
  <v-dialog v-model="open" width="650px">
    <v-card dense>
      <v-toolbar elevation="0">
        <v-toolbar-title
          class="text--subtitle error--text"
          style="padding-left: 10px"
        >
          {{
            $t('message.playlist.modal.remove.title', {
              name: removingPlaylist.name,
            })
          }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-toolbar-items>
          <v-btn :disabled="isFetching" icon @click="handleClose()">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-card-text class="px-7 py-0">
        <v-row no-gutters>
          <v-col cols="12">
            <v-checkbox
              v-model="checkboxConfirmation"
              class="mt-2"
              hide-details
            >
              <template v-slot:label>
                <span style="font-size: 14px">{{
                  $t('message.pages.video.deleteUserModal.checkboxText')
                }}</span>
              </template>
            </v-checkbox>
          </v-col>
          <v-col cols="12" class="my-3">
            <b>{{
              $t('message.pages.video.deleteUserModal.confirmRemoveMessage2')
            }}</b>
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="password"
              class="pr-2"
              :label="$t('message.password')"
              dense
              outlined
              autofocus
              autocomplete="off"
              :error="errors.invalidAccountError"
              :messages="loginPasswordErrorMessage"
              :append-icon="showPass ? 'mdi-eye' : 'mdi-eye-off'"
              :type="showPass ? 'text' : 'password'"
              @keyup.enter="handleDeletePlaylist"
              @click:append="showPass = !showPass"
            >
              <template v-slot:message="{ message }">
                <span
                  class="red--text caption align-text"
                  v-if="errors.invalidAccountError"
                >
                  <v-icon color="red" size="18" class="pb-1">
                    mdi-alert-circle
                  </v-icon>
                  {{ message }}
                </span>
              </template>
            </v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
      <v-col cols="12" class="pa-0 ma-0 pl-8">
        <v-card flat height="30">
          <span class="red--text caption align-text d-block my-1">
            <span v-if="errors.removeError">
              {{ $t('message.errors.unexpectedError') }}
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon size="18" v-bind="attrs" v-on="on">
                    mdi-help-circle
                  </v-icon>
                </template>
                <v-container class="text-body-2 pa-0" style="max-width: 230px">
                  {{ errMsg.removeErrorMsg }}
                </v-container>
              </v-tooltip>
            </span>
          </span>
        </v-card>
      </v-col>
      <v-card-actions class="px-7 pb-6">
        <v-spacer></v-spacer>
        <v-btn class="px-6" text @click="handleClose" :disabled="isFetching">
          {{ $t('message.cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          :disabled="isFetching || !checkboxConfirmation || !password"
          :loading="isFetching"
          @click="handleDeletePlaylist"
        >
          <span class="px-5">{{ $t('message.confirm') }}</span>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapMutations } from 'vuex'
import playlistApi from '@/services/playlistApi'
import i18n from '@/translations/lang'

export default {
  name: 'RemoveModal',
  components: {},
  data: () => ({
    open: false,
    isFetching: false,
    removingPlaylist: {},
    checkboxConfirmation: false,
    showPass: false,
    password: '',
    errors: {
      invalidAccountError: false,
      unexpectedError: false,
      internalErrorException: false,
      removeError: false,
      errMsg: '',
    },
    errMsg: {
      removeErrorMsg: '',
    },
    unknownError: '',
    type: '',
  }),
  computed: {
    userInfo() {
      return this.$store.getters['session/userAttributes']
    },
    loginPasswordErrorMessage() {
      if (this.errors.invalidAccountError) {
        return i18n.t(
          'message.pages.video.deleteUserModal.errors.invalidAccountError'
        )
      }
      if (this.errors.internalErrorException) {
        return i18n.t(
          'message.pages.video.deleteUserModal.errors.internalError'
        )
      }
      if (this.errors.unexpectedError) {
        return i18n.t('message.errors.unexpectedError') + this.unknownError
      }
      return ''
    },
  },
  mounted() {
    this.$root.$on('removePlaylistModal', (playlist) => {
      Object.assign(this.$data, this.initialState())
      this.removingPlaylist = playlist
      this.open = true
    })
  },
  beforeDestroy() {
    this.$root.$off('removePlaylistModal')
  },
  methods: {
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    ...mapActions('session', ['signInUser', 'fetchSession']),
    async handleDeletePlaylist() {
      this.isFetching = true
      let confirmResponse = await this.confirmUserPassword()
      if (!confirmResponse) return
      let playlist = this.removingPlaylist
      try {
        await playlistApi.removePlaylist(playlist.id)
        this.$root.$emit('removePlaylistListing', playlist)
        this.isFetching = false
      } catch (e) {
        this.isFetching = false
        this.errMsg.removeErrorMsg =
          e.response.data.errMsg.message + '. ' + e.response.data.errCode
        this.errors.removeError = true
        return
      }
      this.open = false
    },
    async confirmUserPassword() {
      try {
        await this.signInUser({
          username: this.userInfo.email,
          password: this.password,
        })
        return true
      } catch (error) {
        if (!error.code) {
          const anyError = JSON.parse(JSON.stringify(error))
          if (anyError.message.includes('Network Error')) {
            this.changePasswordLoading = false
            this.unknownError = 'Code: 500'
            this.newPasswordError.newPasswordError = true
            return
          }
        }
        if (
          error.code === 'NotAuthorizedException' ||
          error.code === 'InvalidParameterException'
        ) {
          this.errors.invalidAccountError = true
        }
        if (error.code === 'InternalErrorException') {
          this.errors.internalErrorException = true
        }
        this.isFetching = false
        return false
      }
    },
    handleClose() {
      this.open = false
    },
    initialState() {
      return {
        open: false,
        isFetching: false,
        removingPlaylist: {},
        checkboxConfirmation: false,
        showPass: false,
        password: '',
        errors: {
          invalidAccountError: false,
          unexpectedError: false,
          internalErrorException: false,
          removeError: false,
          errMsg: '',
        },
        errMsg: {
          removeErrorMsg: '',
        },
        unknownError: '',
        type: '',
      }
    },
  },
}
</script>

<style></style>
